import { createAction } from '@reduxjs/toolkit';

import type { Country, ServicePoint } from '@/app/store/slices/cacheSlice';
import type { ServicePointProfile } from '@/app/store/slices/servicePointProfileSlice';
import type { AuthState } from '@/features/auth/authSlice';

/**
 * The subset of cache state that is persisted in the snapshot.
 * Mirrors the selection performed by selectShellCacheSnapshot in stateSnapshotConfig.ts.
 */
interface PersistedCacheSnapshot {
    countries?: Country[];
    /** Language entries from the data-list API — shape varies by locale, kept flexible. */
    languages?: unknown[];
    servicePoint?: ServicePoint[];
    loginCountryLanguage?: Array<{
        cd: string;
        lbl?: string | null;
        dsc?: string | null;
        prntCd?: string | null;
    }>;
}

/**
 * The subset of activeMenuStore state that is persisted in the snapshot.
 * isSnapshotHydrated is never persisted — it is a runtime-only flag.
 */
interface PersistedActiveMenuSnapshot {
    activeComponent?: string;
    activeResourceActions?: string[];
}

/**
 * Exact shape of the payload carried by hydrateSnapshot.
 * Mirrors the object produced by selectStateSnapshotSlices — every key is
 * optional because the snapshot may be absent (empty {}) or partially written.
 *
 * Using Partial<ServicePointProfile> instead of ServicePointProfile because a
 * partially-written snapshot may only contain srvPntFcId without the full profile.
 */
export interface SnapshotPayload {
    authStore?: Partial<AuthState>;
    activeMenuStore?: PersistedActiveMenuSnapshot;
    servicePointProfile?: { profile: ServicePointProfile | Partial<ServicePointProfile> | null };
    cache?: PersistedCacheSnapshot;
}

/**
 * Dispatched once at app startup after the async AES-GCM localStorage snapshot
 * is decrypted. Each slice that participates in snapshot hydration handles this
 * action in its own extraReducers — keeping hydration logic co-located with the
 * slice state it affects.
 *
 * Always dispatched — even with an empty payload {} when no snapshot exists —
 * so that slices can mark themselves as hydrated and unblock any UI that waits
 * for snapshot resolution (e.g. the service-point modal auto-prompt guard).
 *
 * Defined in a standalone file to avoid circular imports between store.ts and
 * the slice files that import it.
 */
export const hydrateSnapshot = createAction<SnapshotPayload>('app/hydrateSnapshot');
